import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import API from "Payments/PaymentPage/API";
import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";

import FnrLoadingBricks from "UI/FnrLoadingBricks2";

import ResendCodeButton from "./ResendCodeButton";
import { useSetAuthToLoggedIn } from "./useSetAuthToLoggedIn";

CodeInput.propTypes = {
  code: PropTypes.string,
  handleCodeChange: PropTypes.func,
  setErrorCode: PropTypes.func,
};

export default function CodeInput({
  hasAnimatedIn,
  code,
  handleCodeChange,
  setErrorCode,
  onResendComplete,
}) {
  const inputRef = useRef();

  const { state, dispatch } = usePaymentPage();
  const setAuthToLoggedIn = useSetAuthToLoggedIn();
  const { loginValue, loginValueType } = state.authCodeData;
  const { fundColorSafe } = state.fund;
  const { tabID, fund } = state;

  const [shouldSubmit, setShouldSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (hasAnimatedIn) inputRef.current.focus();
  }, [hasAnimatedIn]);

  useEffect(() => {
    if (code.length === 4) {
      setTimeout(() => {
        setShouldSubmit(true);
      }, 200);
    }
  }, [code]);

  useEffect(() => {
    if (!shouldSubmit) return;

    const payload = {
      fundID: fund.ID,
      tabID,
      code,
    };
    if (loginValueType === "phone") {
      payload.phone = loginValue;
    } else {
      payload.email = loginValue;
    }

    const loginCall = API.loginWithAuthCode(payload);

    setIsLoading(true);
    loginCall
      .post()
      .then((res) => {
        setShouldSubmit(false);
        dispatch({
          type: "setPaymentMethods",
          payload: res.paymentMethods,
        });
        dispatch({
          type: "setUser",
          payload: res.user,
        });
        setAuthToLoggedIn({ hasPaymentMethod: res.paymentMethods?.length > 0 });
        window.data.paymentPageData.user = res.user;
        window.data.paymentPageData.campaign = res.campaign;
      })
      .catch(() => {
        setErrorCode("invalidTextCode");
        setIsLoading(false);
        setShouldSubmit(false);
      });

    return () => {
      if (loginCall.cancel) loginCall.cancel();
    };
  }, [
    shouldSubmit,
    code,
    loginValueType,
    loginValue,
    dispatch,
    setErrorCode,
    setAuthToLoggedIn,
    tabID,
    fund.ID,
  ]);

  return (
    <Wrapper>
      <FnrLoadingBricks loading={isLoading} color={fundColorSafe}>
        <BeforeResend>
          {code.length === 0 && (
            <>
              <Placeholder>
                <Circle />
                <Circle />
                <Circle />
                <Circle />
              </Placeholder>
            </>
          )}
          <Input
            data-cy="code"
            ref={inputRef}
            value={code}
            onChange={handleCodeChange}
            disabled={isLoading}
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            style={{ textIndent: code.length > 0 ? 12 : 0 }}
          />
        </BeforeResend>
        <ResendCodeButton
          onResendComplete={onResendComplete}
          setErrorCode={setErrorCode}
        />
      </FnrLoadingBricks>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  padding: 5px;
  border: 2px solid #bec3c7;
  border-radius: 9px;
  display: flex;
  align-items: center;
  overflow: hidden;
  max-width: 200px;
  margin: 0 auto;
  transition: border 0.6s;
  position: relative;
  &:hover,
  &:focus-within {
    transition: border 0.2s;
    border: 2px solid #278dc1;
  }
`;

const Input = styled.input`
  box-sizing: border-box;
  text-align: center;
  width: 100%;
  font-size: 50px;
  line-height: 50px;
  padding: 8px;
  font-weight: 600;
  color: #474747;
  border-radius: 5px;
  background: none;
  border: none;
  font-variant-numeric: tabular-nums;
  outline: none;
  letter-spacing: 12px;
  text-indent: 12px;
  position: relative;
  z-index: 100;
`;

const Placeholder = styled.div`
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0 14px;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;
const Circle = styled.div`
  background: #98a4a5;
  width: 18px;
  height: 18px;
  border-radius: 20px;
`;
const BeforeResend = styled.div`
  position: relative;
`;
